import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2, Trash2, Copy, Check, KeyRound, Webhook as WebhookIcon, Activity, Building2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { useAuth } from "@/lib/auth";

export default function AdminPlatform() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  return (
    <div className="max-w-5xl space-y-6" data-testid="platform-page">
      <div>
        <div className="axia-overline">Admin · Platform</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Developer platform</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">Issue API tokens, register outbound webhooks, and integrate Axia with your stack.</p>
      </div>
      <Tabs defaultValue="tokens">
        <TabsList>
          <TabsTrigger value="tokens" data-testid="tab-tokens"><KeyRound className="w-3.5 h-3.5 mr-1.5" /> API tokens</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks"><WebhookIcon className="w-3.5 h-3.5 mr-1.5" /> Webhooks</TabsTrigger>
          {isSuper && (
            <TabsTrigger value="tenants" data-testid="tab-tenants"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Tenants</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="tokens" className="pt-4"><Tokens /></TabsContent>
        <TabsContent value="webhooks" className="pt-4"><Webhooks /></TabsContent>
        {isSuper && (
          <TabsContent value="tenants" className="pt-4"><Tenants /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Tokens() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const reload = async () => { const r = await api.get("/platform/tokens"); setItems(r.data.items || []); };
  useEffect(() => { reload(); }, []);

  const revoke = async (id) => {
    if (!confirm("Revoke this token? Any service using it will break.")) return;
    await api.delete(`/platform/tokens/${id}`);
    toast.success("Revoked");
    reload();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-token-btn"><Plus className="w-4 h-4 mr-2" /> New token</Button>
          </DialogTrigger>
          <NewTokenDialog onCreated={(tok) => { setOpen(false); setCreated(tok); reload(); }} />
        </Dialog>
      </div>
      {created && (
        <div className="axia-card p-4 border-emerald-300 bg-emerald-50/40" data-testid="created-token-banner">
          <div className="text-sm font-medium text-emerald-900">Token created — copy it now, you won't see it again.</div>
          <div className="mt-2 flex items-center gap-2 font-mono text-xs">
            <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-md break-all">{created.token}</code>
            <CopyBtn text={created.token} />
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500" data-testid="empty-tokens">
          <KeyRound className="w-7 h-7 mx-auto text-slate-300" />
          <p className="text-sm mt-3">No API tokens yet.</p>
        </div>
      ) : (
        <ul className="axia-card divide-y divide-slate-100">
          {items.map((t) => (
            <li key={t.id} className="px-5 py-3 flex items-center gap-3" data-testid={`token-row-${t.id}`}>
              <KeyRound className="w-4 h-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 text-sm">{t.name}</div>
                <div className="text-xs text-slate-500 font-mono">{t.prefix}…</div>
              </div>
              <div className="text-xs text-slate-500">{t.scopes?.join(", ")}</div>
              <div className="text-xs text-slate-400">{t.last_used_at ? `used ${format(parseISO(t.last_used_at), "d MMM")}` : "never used"}</div>
              <Button variant="ghost" size="sm" onClick={() => revoke(t.id)} className="text-rose-600 hover:text-rose-700" data-testid={`revoke-${t.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewTokenDialog({ onCreated }) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState("read");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name) return toast.error("Name required");
    setSaving(true);
    try {
      const r = await api.post("/platform/tokens", { name, scopes: [scope] });
      toast.success("Token created");
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-md" data-testid="new-token-dialog">
      <DialogHeader>
        <DialogTitle>New API token</DialogTitle>
        <DialogDescription>Bearer-style token for Axia public REST API.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Looker dashboard" data-testid="token-name-input" />
        </div>
        <div>
          <Label>Scope</Label>
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={() => setScope("read")} className={`px-3 py-1.5 rounded-md text-xs border ${scope === "read" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`} data-testid="scope-read">read-only</button>
            <button type="button" onClick={() => setScope("write")} className={`px-3 py-1.5 rounded-md text-xs border ${scope === "write" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`} data-testid="scope-write">read + write</button>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="create-token-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Create token
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Webhooks() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [deliveries, setDeliveries] = useState({});

  const reload = async () => {
    const r = await api.get("/platform/webhooks");
    setItems(r.data.items || []);
    setEvents(r.data.events || []);
  };
  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this webhook?")) return;
    await api.delete(`/platform/webhooks/${id}`);
    reload();
  };

  const showDeliveries = async (id) => {
    if (deliveries[id]) { setDeliveries((p) => ({ ...p, [id]: null })); return; }
    const r = await api.get(`/platform/webhooks/${id}/deliveries`);
    setDeliveries((p) => ({ ...p, [id]: r.data.items || [] }));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-webhook-btn"><Plus className="w-4 h-4 mr-2" /> New webhook</Button>
          </DialogTrigger>
          <NewWebhookDialog events={events} onCreated={(w) => { setOpen(false); setCreated(w); reload(); }} />
        </Dialog>
      </div>
      {created && (
        <div className="axia-card p-4 border-emerald-300 bg-emerald-50/40" data-testid="created-webhook-banner">
          <div className="text-sm font-medium text-emerald-900">Webhook signing secret — copy it now.</div>
          <div className="mt-2 flex items-center gap-2 font-mono text-xs">
            <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-md break-all">{created.secret}</code>
            <CopyBtn text={created.secret} />
          </div>
          <div className="mt-2 text-xs text-slate-600">Use this to verify the <code>X-Axia-Signature</code> HMAC-SHA256 header on incoming POSTs.</div>
        </div>
      )}
      {items.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500" data-testid="empty-webhooks">
          <WebhookIcon className="w-7 h-7 mx-auto text-slate-300" />
          <p className="text-sm mt-3">No webhooks registered.</p>
        </div>
      ) : (
        <ul className="axia-card divide-y divide-slate-100">
          {items.map((w) => (
            <li key={w.id} className="px-5 py-3" data-testid={`webhook-row-${w.id}`}>
              <div className="flex items-center gap-3">
                <WebhookIcon className="w-4 h-4 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-slate-900 truncate">{w.url}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{(w.events || []).join(", ")}</div>
                </div>
                <span className={`axia-pill ${w.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {w.is_active ? "active" : "paused"}
                </span>
                <Button variant="ghost" size="sm" onClick={() => showDeliveries(w.id)} className="h-7 px-2 text-xs" data-testid={`deliveries-${w.id}`}>
                  <Activity className="w-3 h-3 mr-1" /> {deliveries[w.id] !== undefined && deliveries[w.id] !== null ? "Hide" : "Deliveries"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(w.id)} className="text-rose-600 hover:text-rose-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              {deliveries[w.id] && (
                <ul className="mt-3 space-y-1 text-xs">
                  {(deliveries[w.id] || []).length === 0 && <li className="text-slate-400">No deliveries yet.</li>}
                  {(deliveries[w.id] || []).map((d) => (
                    <li key={d.id} className="flex items-center gap-2 font-mono">
                      <span className={`px-1.5 py-0.5 rounded ${d.status === "delivered" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{d.status_code || "—"}</span>
                      <span className="text-slate-700">{d.event}</span>
                      <span className="text-slate-400">{d.last_attempt_at ? format(parseISO(d.last_attempt_at), "d MMM HH:mm") : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewWebhookDialog({ events, onCreated }) {
  const [url, setUrl] = useState("https://");
  const [chosen, setChosen] = useState([]);
  const [saving, setSaving] = useState(false);
  const toggle = (e) => setChosen((p) => p.includes(e) ? p.filter((x) => x !== e) : [...p, e]);
  const submit = async () => {
    if (!url || chosen.length === 0) return toast.error("URL and at least one event required");
    setSaving(true);
    try {
      const r = await api.post("/platform/webhooks", { url, events: chosen });
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-lg" data-testid="new-webhook-dialog">
      <DialogHeader>
        <DialogTitle>Register webhook</DialogTitle>
        <DialogDescription>Outbound POST to your endpoint when listed events fire.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Endpoint URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="webhook-url-input" />
        </div>
        <div>
          <Label>Events</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="webhook-events">
            {(events || []).map((e) => {
              const sel = chosen.includes(e);
              return (
                <button type="button" key={e} onClick={() => toggle(e)} className={`px-2 py-1 rounded-md text-[11px] font-mono border ${sel ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`} data-testid={`event-${e}`}>{e}</button>
              );
            })}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="create-webhook-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Register webhook
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <Button variant="outline" size="sm" onClick={async () => { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }} data-testid="copy-secret-btn">
      {done ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />} {done ? "Copied" : "Copy"}
    </Button>
  );
}


/* ====================================================================== */
/* Tenants (super-admin only)                                              */
/* ====================================================================== */
function Tenants() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tenants");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-3" data-testid="tenants-pane">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Each tenant is an isolated workspace. Creating one auto-invites the first admin and gives them their own subdomain to claim.
        </p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreated(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-tenant-btn"><Plus className="w-4 h-4 mr-2" /> New tenant</Button>
          </DialogTrigger>
          <NewTenantDialog
            onCreated={(payload) => { setCreated(payload); reload(); }}
            created={created}
            onClose={() => { setOpen(false); setCreated(null); }}
          />
        </Dialog>
      </div>

      <div className="axia-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Name</th>
              <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Slug</th>
              <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Subdomain</th>
              <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Plan</th>
              <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No tenants yet.</td></tr>
            )}
            {items.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`tenant-row-${t.slug}`}>
                <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <div className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-bold" style={{ background: t.primary_color || "#0F172A" }}>
                    {(t.name || "?")[0].toUpperCase()}
                  </div>
                  {t.name}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{t.slug}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">
                  {t.subdomain ? (
                    <a href={`/preview/${t.subdomain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" data-testid={`tenant-preview-${t.slug}`}>
                      {t.subdomain} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-slate-400 italic">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{t.plan || "trial"}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {t.created_at ? format(parseISO(t.created_at), "MMM d, yyyy") : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <SwitchToTenantBtn tenantId={t.id} tenantName={t.name} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SwitchToTenantBtn({ tenantId, tenantName }) {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    if (!confirm(`Switch your active workspace to '${tenantName}'? You'll be reloaded into that context.`)) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/switch-tenant", { tenant_id: tenantId });
      localStorage.setItem("axia.access", r.data.access_token);
      localStorage.setItem("axia.refresh", r.data.refresh_token || "");
      localStorage.setItem("axia.user", JSON.stringify(r.data.user || {}));
      toast.success(`Switched to ${tenantName}`);
      window.location.href = "/";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch tenant");
    } finally { setLoading(false); }
  };
  return (
    <Button variant="ghost" size="sm" onClick={onClick} disabled={loading} data-testid={`tenant-switch-${tenantId}`}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Open"}
    </Button>
  );
}

function NewTenantDialog({ onCreated, created, onClose }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState("#0F172A");
  const [adminEmail, setAdminEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!slug || !name) return toast.error("Slug and name are required");
    if (!/^[a-z0-9-]+$/.test(slug)) return toast.error("Slug: lowercase letters, digits, hyphens only");
    setSaving(true);
    try {
      const r = await api.post("/tenants", {
        slug: slug.trim().toLowerCase(),
        name: name.trim(),
        primary_color: color,
        admin_email: adminEmail.trim().toLowerCase() || undefined,
      });
      toast.success(`Tenant '${r.data.name}' created`);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create tenant");
    } finally { setSaving(false); }
  };

  if (created) {
    return (
      <DialogContent data-testid="new-tenant-result">
        <DialogHeader>
          <DialogTitle>Tenant created</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-slate-900">{created.name}</span> is live.
            {created.invite_link && " The first admin's invite link is below — share it manually if the email doesn't arrive."}
          </DialogDescription>
        </DialogHeader>
        {created.invite_link && (
          <div className="space-y-2">
            <Label>First admin invite link (48 hour expiry)</Label>
            <div className="flex items-center gap-2">
              <Input readOnly value={created.invite_link} className="font-mono text-xs" data-testid="invite-link-input" />
              <CopyBtn text={created.invite_link} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="new-tenant-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent data-testid="new-tenant-dialog">
      <DialogHeader>
        <DialogTitle>Create a new tenant</DialogTitle>
        <DialogDescription>Each tenant is an isolated workspace with its own users, cycles, frameworks, and branding.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label htmlFor="t-name">Workspace name</Label>
          <Input id="t-name" data-testid="new-tenant-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" />
        </div>
        <div>
          <Label htmlFor="t-slug">Slug</Label>
          <Input id="t-slug" data-testid="new-tenant-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="acme" />
          <p className="text-[11px] text-slate-500 mt-1">Internal identifier. Lowercase letters, digits, hyphens.</p>
        </div>
        <div>
          <Label htmlFor="t-color">Primary color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" id="t-color" value={color} onChange={(e) => setColor(e.target.value)} className="w-10 h-10 rounded border border-slate-200" />
            <Input data-testid="new-tenant-color" value={color} onChange={(e) => setColor(e.target.value)} className="font-mono" />
          </div>
        </div>
        <div>
          <Label htmlFor="t-admin">First admin email <span className="text-slate-400">(optional)</span></Label>
          <Input id="t-admin" data-testid="new-tenant-admin" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="founder@acme.com" />
          <p className="text-[11px] text-slate-500 mt-1">If provided, they'll be invited as Tenant Admin with a 48-hour magic link.</p>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="new-tenant-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
          Create tenant
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
